import {v4 as uuidv4} from 'uuid';
import {store} from 'src/redux/store';
import {isEffectivelyOffline} from './connectivity';
import {offlineDb} from './db';
import {PROTECTED_SNAPSHOT_KEYS} from './snapshotKeys';
import type {OfflineSession} from './types';

export interface HydrationPlanEntry {
  key: string;
  run: () => Promise<unknown>;
}

export interface StartOfflineSessionArgs {
  contextKey?: string;
  hydrationPlan?: HydrationPlanEntry[];
}

export interface SessionIdentity {
  instanceId: number;
  userId: number;
}

/**
 * The signed-in instance+user from redux, or undefined unless BOTH are present.
 * Never returns a partial identity so `undefined === undefined` can't match.
 */
export const currentIdentity = (): SessionIdentity | undefined => {
  const {user, instance} = store.getState().session;
  if (user?.id == null || instance?.id == null) {
    return undefined;
  }
  return {instanceId: instance.id, userId: user.id};
};

/** True only when the session belongs to the currently signed-in instance+user. */
export const isOwnedSession = (session: OfflineSession): boolean => {
  const identity = currentIdentity();
  if (!identity) {
    return false;
  }
  return (
    session.instanceId === identity.instanceId &&
    session.userId === identity.userId
  );
};

type Hydrator = () => Promise<unknown>;

/**
 * Global hydration steps run once, online, when any session starts. Domain
 * modules register keys via `registerHydrator`; only auth-context lives here.
 */
const hydrators = new Map<string, Hydrator>();

export const registerHydrator = (key: string, hydrator: Hydrator): void => {
  hydrators.set(key, hydrator);
};

registerHydrator('auth-context', async () => {
  const {user, instance, isSuperAdmin} = store.getState().session;
  return {user, instance, isSuperAdmin};
});

const writeSnapshots = async (
  sessionId: string,
  entries: HydrationPlanEntry[]
): Promise<void> => {
  const settled = await Promise.allSettled(
    entries.map(async ({key, run}) => {
      const data = await run();
      await offlineDb.snapshots.put({
        key,
        sessionId,
        data,
        cachedAt: new Date().toISOString(),
      });
    })
  );

  const firstRejection = settled.find(
    (result): result is PromiseRejectedResult => result.status === 'rejected'
  );
  if (firstRejection) {
    throw firstRejection.reason;
  }
};

const runHydration = (
  sessionId: string,
  plan: HydrationPlanEntry[]
): Promise<void> =>
  writeSnapshots(sessionId, [
    ...Array.from(hydrators.entries()).map(([key, run]) => ({key, run})),
    ...plan,
  ]);

/** Re-runs a hydration plan into an existing session, refreshing its snapshots
 * in parallel. Global hydrators are not re-run — only the given plan. */
export const rehydrateSession = async (
  sessionId: string,
  plan: HydrationPlanEntry[]
): Promise<void> => {
  await writeSnapshots(sessionId, plan);
  await offlineDb.sessions.update(sessionId, {
    hydratedAt: new Date().toISOString(),
  });
  // Drop snapshots no longer in the plan (e.g. a deleted definition), keeping
  // global hydrator keys and sync-state keys the plan never lists.
  const keep = new Set([
    ...plan.map(entry => entry.key),
    ...hydrators.keys(),
    ...PROTECTED_SNAPSHOT_KEYS,
  ]);
  await offlineDb.snapshots
    .where('sessionId')
    .equals(sessionId)
    .filter(snapshot => !keep.has(snapshot.key))
    .delete();
};

/**
 * Active session for `contextKey`, ignoring ownership. Only janitor/purge and
 * tests should use this; everything else goes through `getActiveSession`.
 */
export const getActiveSessionUnscoped = async (
  contextKey?: string
): Promise<OfflineSession | undefined> => {
  if (contextKey === undefined) {
    return offlineDb.sessions
      .where('status')
      .equals('active')
      .filter(session => session.contextKey === undefined)
      .first();
  }
  return offlineDb.sessions
    .where('contextKey')
    .equals(contextKey)
    .filter(session => session.status === 'active')
    .first();
};

/** Active owned session for `contextKey`; undefined when identity is missing or
 * the matching session belongs to another user. */
export const getActiveSession = async (
  contextKey?: string
): Promise<OfflineSession | undefined> => {
  const session = await getActiveSessionUnscoped(contextKey);
  return session && isOwnedSession(session) ? session : undefined;
};

/** Owned session for `contextKey` only once it is ready to serve reads/writes;
 * a storage failure reads as "no session" so callers fall through to network. */
export const getReadySession = async (
  contextKey?: string
): Promise<OfflineSession | undefined> => {
  try {
    const session = await getActiveSession(contextKey);
    return session?.ready ? session : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Starts a session for the signed-in user. The duplicate guard is owned-scoped:
 * contextKey uniqueness is per-user, so two users may each hold one.
 */
export const startOfflineSession = async ({
  contextKey,
  hydrationPlan,
}: StartOfflineSessionArgs = {}): Promise<OfflineSession> => {
  if (isEffectivelyOffline()) {
    throw new Error('Cannot start an offline session while offline.');
  }

  const identity = currentIdentity();
  if (!identity) {
    throw new Error('Cannot start an offline session without a signed-in user.');
  }

  const duplicateMessage =
    contextKey === undefined
      ? 'An offline session is already active.'
      : 'An offline session is already active for this context.';

  if (await getActiveSession(contextKey)) {
    throw new Error(duplicateMessage);
  }

  const sessionId = uuidv4();

  try {
    await runHydration(sessionId, hydrationPlan ?? []);
  } catch (error) {
    await offlineDb.snapshots.where('sessionId').equals(sessionId).delete();
    throw error;
  }

  const startedAt = new Date().toISOString();
  const session: OfflineSession = {
    id: sessionId,
    instanceId: identity.instanceId,
    userId: identity.userId,
    startedAt,
    hydratedAt: startedAt,
    status: 'active',
    ready: hydrationPlan === undefined,
    contextKey,
  };

  try {
    await offlineDb.transaction('rw', offlineDb.sessions, async () => {
      if (await getActiveSession(contextKey)) {
        throw new Error(duplicateMessage);
      }
      await offlineDb.sessions.add(session);
    });
  } catch (error) {
    await offlineDb.snapshots.where('sessionId').equals(sessionId).delete();
    throw error;
  }

  return session;
};

/** Marks a session ready once its multi-step hydration/download completes. */
export const markSessionReady = async (sessionId: string): Promise<void> => {
  const updated = await offlineDb.sessions.update(sessionId, {ready: true});
  if (!updated) {
    throw new Error('Cannot mark a session that no longer exists as ready.');
  }
};

/**
 * Closes a session — only allowed once its outbox is fully drained (no
 * pending, failed, or syncing items).
 */
export const closeOfflineSession = async (sessionId: string): Promise<void> => {
  await offlineDb.transaction(
    'rw',
    [offlineDb.sessions, offlineDb.outbox, offlineDb.files],
    async () => {
      const session = await offlineDb.sessions.get(sessionId);
      if (!session) {
        throw new Error('Cannot close a session that no longer exists.');
      }
      if (session.status !== 'active') {
        throw new Error('Cannot close a session that is not active.');
      }

      const outstanding = await offlineDb.outbox
        .where('sessionId')
        .equals(sessionId)
        .filter(
          item =>
            item.status === 'pending' ||
            item.status === 'failed' ||
            item.status === 'syncing'
        )
        .count();

      if (outstanding > 0) {
        throw new Error(
          'Cannot close a session with pending or failed outbox items.'
        );
      }

      // Cached + captured blobs are dead once drained — free the quota they hold.
      await offlineDb.files.where('sessionId').equals(sessionId).delete();

      await offlineDb.sessions.update(sessionId, {
        status: 'closed',
        closedAt: new Date().toISOString(),
      });
    }
  );
};

/**
 * Deletes a session and all of its outbox items, snapshots, and files. Escape
 * hatch for abandoning a session — not part of the normal close-out.
 */
export const discardOfflineSession = async (
  sessionId: string
): Promise<void> => {
  await offlineDb.transaction(
    'rw',
    [offlineDb.sessions, offlineDb.outbox, offlineDb.snapshots, offlineDb.files],
    async () => {
      const syncing = await offlineDb.outbox
        .where('sessionId')
        .equals(sessionId)
        .filter(item => item.status === 'syncing')
        .count();

      if (syncing > 0) {
        throw new Error('Cannot discard a session with items mid-sync.');
      }

      await offlineDb.outbox.where('sessionId').equals(sessionId).delete();
      await offlineDb.snapshots.where('sessionId').equals(sessionId).delete();
      await offlineDb.files.where('sessionId').equals(sessionId).delete();
      await offlineDb.sessions.delete(sessionId);
    }
  );
};
